import { useEffect, useMemo } from 'react';

/**
 * Auth form enter animations must not replay when React Strict Mode remounts
 * (dev) or when a parent stage remounts mid-enter. A module Set survives that
 * remount; keys are cleared on real navigation away (deferred so Strict Mode
 * remount still sees them and skips a second enter).
 */
const played = new Set<string>();

/** True once — subsequent mounts with the same key skip `initial="hidden"`. */
export function takeAuthEntrance(key: string): boolean {
  if (played.has(key)) return false;
  played.add(key);
  return true;
}

/** Stable for this mount/key; safe to use as Motion `initial` gate. */
export function useAuthEntrance(key: string): boolean {
  return useMemo(() => takeAuthEntrance(key), [key]);
}

/**
 * Clear entrance keys after this component unmounts. Call from a `useEffect`
 * cleanup only — never on mount (that cancelled the clear on leave and left
 * keys stuck, so login → forgot → login skipped animation).
 */
export function releaseAuthEntrances(keys: readonly string[]) {
  window.setTimeout(() => {
    for (const key of keys) played.delete(key);
  }, 0);
}

/** `useEffect(() => useReleaseAuthEntrances(keys), [])` — clear on unmount. */
export function useReleaseAuthEntrances(keys: readonly string[]) {
  useEffect(() => {
    return () => releaseAuthEntrances(keys);
  }, [keys]);
}
